import React, { useCallback, useEffect, useState } from 'react';
import CompanyLocationSelector from '../../Components/CompanyLocationSelector';
import Sidebar from '../../Components/Sidebar';
import trident from '../../assets/odin-trident.png';
import axios from '../../api/axiosInstance';
import AdminEquipmentProfileSlideOut from '../../Components/AdminEquipmentProfileSlideOut';
import './AdminPages.css';
import { logout } from '../../auth/cookieUtils';


const EquipmentManagement = () => {
  const [selectedLocationId, setSelectedLocationId] = useState(null);
  const [areas, setAreas] = useState([]);
  const [selectedAreaIds, setSelectedAreaIds] = useState([]);
  const [equipmentList, setEquipmentList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: 'equipmentName', direction: 'asc' });
  const [showModal, setShowModal] = useState(false);
  const [newEquipment, setNewEquipment] = useState({ name: '', serialNumber: '', areaId: '' });
  const [selectedEquipmentId, setSelectedEquipmentId] = useState(null);
  const [profileOpen, setProfileOpen] = useState(false);
    const [page, setPage] = useState(0);          // 0-based page index
  const [pageSize, setPageSize] = useState(25); // rows per page
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [equipmentNameFilter, setEquipmentNameFilter] = useState('');



  useEffect(() => {
    if (!selectedLocationId) {
      setAreas([]);
      setSelectedAreaIds([]);
      return;
    }
    const fetchAreas = async () => {
      try {
        const response = await axios.get(`/api/areas/location/${selectedLocationId}`);
        setAreas(Array.isArray(response.data) ? response.data : []);
        setPage(0);
        setSelectedAreaIds([]);
      } catch (error) {
        console.error('Error fetching areas:', error);
      }
    };
    fetchAreas();
  }, [selectedLocationId]);

    const fetchEquipment = useCallback(async () => {
    if (!selectedLocationId) {
      setEquipmentList([]);
      setTotalPages(0);
      setTotalElements(0);
      return;
    }

    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set('locationId', selectedLocationId);
      params.set('page', page);
      params.set('size', pageSize);
      params.set('sortBy', sortConfig.key);
      params.set('sortDirection', sortConfig.direction);

      selectedAreaIds.forEach((id) => params.append('areaIds', id));

      if (equipmentNameFilter.trim() !== '') {
      params.set('equipmentName', equipmentNameFilter.trim());
    }

    const response = await axios.get(
      `/api/equipment/descriptions/search-paginated?${params.toString()}`
    );
    const data = response.data;

      // Spring Page<T> shape
      const content = Array.isArray(data?.content) ? data.content : [];
      setEquipmentList(content);
      setTotalPages(data?.totalPages ?? 0);
      setTotalElements(data?.totalElements ?? 0);
    } catch (error) {
      console.error('Error fetching equipment:', error);
      setEquipmentList([]);
      setTotalPages(0);
      setTotalElements(0);
    } finally {
      setLoading(false);
    }
  }, [selectedLocationId, page, pageSize, sortConfig, selectedAreaIds, equipmentNameFilter]);


   useEffect(() => {
  fetchEquipment();
}, [fetchEquipment]);

useEffect(() => {
  setPage(0);
}, [selectedLocationId, selectedAreaIds, sortConfig, pageSize, equipmentNameFilter]);



  const toggleAreaSelection = (areaId) => {
    setSelectedAreaIds((prev) =>
      prev.includes(areaId) ? prev.filter((id) => id !== areaId) : [...prev, areaId]
    );
  };

  const handleSort = (key) => {
    setSortConfig((prevConfig) => {
      const direction = prevConfig.key === key && prevConfig.direction === 'asc' ? 'desc' : 'asc';
      return { key, direction };
    });
  };

  const handleAddEquipment = async () => {
    if (!selectedLocationId) {
      alert('Select a location before adding equipment.');
      return;
    }
    if (!newEquipment.name.trim() || !newEquipment.areaId) {
      alert('Equipment name and area are required.');
      return;
    }
    try {
      await axios.post(`/api/equipment/add/${selectedLocationId}`, {
        ...newEquipment,
        name: newEquipment.name.trim(),
        serialNumber: newEquipment.serialNumber.trim(),
      });
      setShowModal(false);
      setNewEquipment({ name: '', serialNumber: '', areaId: '' });
      fetchEquipment();
    } catch (error) {
      console.error('Error adding equipment:', error);
      alert(error.response?.data?.message || 'Unable to add equipment. Please try again.');
    }
  };

  const handleRowClick = (equipmentId) => {
    setSelectedEquipmentId(equipmentId);
    setProfileOpen(true);
  };

  return (
    <div className="dashboard-page">
    <div className="dashboard-topbar">
  <div className="topbar-left">
    <img src={trident} alt="Odin Logo" className="navbar-logo" />
    <CompanyLocationSelector
      onLocationChange={setSelectedLocationId}
    />
  </div>

  <div className="topbar-center">
    <div className="page-title">Equipment Management</div>
  </div>

  <div className="topbar-right">
    <button
      className="logout-button"
      onClick={() => {
        logout();
      }}
    >
      Logout
    </button>
  </div>
</div>


      <div className="dashboard-main">
        <Sidebar />

        <div className="dashboard-content">
          <div className="dashboard-filters" style={{ marginBottom: '16px', maxWidth: '100%' }}>
            <h3>Filter by Area</h3>
            <ul className="area-list" style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
              {areas.map((area) => (
                <li
                  key={area.id}
                  onClick={() => toggleAreaSelection(area.id)}
                  className={selectedAreaIds.includes(area.id) ? 'selected' : ''}
                  style={{
                    cursor: 'pointer',
                    padding: '6px 10px',
                    border: '1px solid #4caf50',
                    borderRadius: '4px',
                  }}
                >
                  {area.name}
                </li>
              ))}
            </ul>
          </div>

          <div className="equipment-table-section">
            <div className="equipment-table-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h4 className="equipment-table-title">Equipment</h4>
              {selectedLocationId && (
                <button className="export-pdf-button" onClick={() => setShowModal(true)}>
                  Add Equipment
                </button>
              )}
            </div>
            {selectedLocationId && (
  <div style={{ marginTop: '10px', marginBottom: '5px', display: 'flex', justifyContent: 'flex-start' }}>
    <input
      type="text"
      placeholder="Search by equipment name..."
      value={equipmentNameFilter}
      onChange={(e) => setEquipmentNameFilter(e.target.value)}
      style={{ padding: '6px 10px', borderRadius: 6, minWidth: '260px' }}
    />
  </div>
)}

            {selectedLocationId && (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        margin: '10px 0'
      }}
    >
      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        <button
          className="logout-button"
          onClick={() => setPage((p) => Math.max(0, p - 1))}
          disabled={loading || page === 0}
        >
          Prev
        </button>

        <div style={{ color: '#fff' }}>
          Page <strong>{totalPages === 0 ? 0 : page + 1}</strong> of <strong>{totalPages}</strong>
          {' '}({totalElements} total)
        </div>

        <button
          className="export-pdf-button"
          onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
          disabled={loading || totalPages === 0 || page >= totalPages - 1}
        >
          Next
        </button>
      </div>

      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        <span style={{ color: '#fff' }}>Rows:</span>
        <select
          value={pageSize}
          onChange={(e) => setPageSize(parseInt(e.target.value, 10))}
          style={{ padding: '6px 10px', borderRadius: 6 }}
          disabled={loading}
        >
          {[10, 25, 50, 100].map((n) => (
            <option key={n} value={n}>{n}</option>
          ))}
        </select>
      </div>
    </div>
  )}

  

            <div className="table-scroll-container">
              <table className="equipment-table">
                <thead>
                  <tr>
                    <th onClick={() => handleSort('equipmentName')}>Name</th>
                    <th onClick={() => handleSort('equipmentNumber')}>Equipment Number</th>
                    <th onClick={() => handleSort('areaName')}>Area</th>
                    <th onClick={() => handleSort('riskRating')}>Risk</th>
                    <th onClick={() => handleSort('equipmentType')}>Equipment Type</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr><td colSpan="5">Loading...</td></tr>
                  ) : equipmentList.length === 0 ? (
                    <tr><td colSpan="5">No equipment found.</td></tr>
                  ) : (
                    equipmentList.map((eq) => (
                      <tr key={eq.equipmentId} className="clickable-row" onClick={() => handleRowClick(eq.equipmentId)}>


                        <td>{eq.equipmentName}</td>
                        <td>{eq.equipmentNumber}</td>
                        <td>{eq.areaName || areas.find(area => area.id === eq.areaId)?.name || '—'}</td>
                        <td>{eq.riskRating}</td>
                        <td>{eq.equipmentType}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      {showModal && (
        <div className="modal-overlay">
          <div className="modal">
            <h2>Add New Equipment</h2>
            <label>Name</label>
            <input
              type="text"
              value={newEquipment.name}
              onChange={(e) => setNewEquipment({ ...newEquipment, name: e.target.value })}
            />
            <label>Serial Number</label>
            <input
              type="text"
              value={newEquipment.serialNumber}
              onChange={(e) => setNewEquipment({ ...newEquipment, serialNumber: e.target.value })}
            />
            <label>Area</label>
            <select
              value={newEquipment.areaId}
              onChange={(e) => setNewEquipment({ ...newEquipment, areaId: parseInt(e.target.value) })}
            >
              <option value="">Select an Area</option>
              {areas.map((area) => (
                <option key={area.id} value={area.id}>{area.name}</option>
              ))}
            </select>
            <div style={{ marginTop: '16px' }}>
              <button className="export-pdf-button" onClick={handleAddEquipment}>Add</button>
              <button className="logout-button" onClick={() => setShowModal(false)} style={{ marginLeft: '10px' }}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {profileOpen && (
       <AdminEquipmentProfileSlideOut
       isOpen={profileOpen}
       equipmentId={selectedEquipmentId}
       onClose={() => setProfileOpen(false)}
       areas={areas}
     />
     
      )}
    </div>
  );
};

export default EquipmentManagement;
